import React, { useRef, useState } from 'react';
import { Copy, Check, ChevronLeft, ChevronRight, Loader, Terminal, X } from 'lucide-react';

// Enterprise Light theme brand stops. Wasabi green #48b94c is the accent.
// (Names kept for API compatibility; "purple" now resolves to green.)
const PURPLE_GRADIENT = 'linear-gradient(135deg, #48b94c 0%, #389a3c 100%)';
const GREEN_GRADIENT = 'linear-gradient(135deg, #48b94c 0%, #389a3c 100%)';
const GREEN_GRADIENT_SOFT = 'linear-gradient(135deg, rgba(72, 185, 76, 0.12), rgba(72, 185, 76, 0.04))';

// GlowIconTile - brand-green icon badge (flat, subtle shadow - no neon glow).
// `accent`: 'purple' | 'green' | 'navy'  (purple/green both -> brand green)
export const GlowIconTile = ({ icon: Icon, accent = 'purple', size = 'md', className = '' }) => {
  const dim = size === 'sm' ? 'w-10 h-10' : size === 'lg' ? 'w-16 h-16' : 'w-14 h-14';
  const iconSize = size === 'sm' ? 'w-5 h-5' : size === 'lg' ? 'w-8 h-8' : 'w-7 h-7';

  const palette = {
    purple: {
      background: '#48b94c',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
      iconColor: '#ffffff',
    },
    green: {
      background: '#48b94c',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
      iconColor: '#ffffff',
    },
    navy: {
      background: '#f1f5f9',
      boxShadow: 'inset 0 0 0 1px #e2e8f0',
      iconColor: '#389a3c',
    },
  }[accent];

  return (
    <div
      className={`${dim} rounded-xl flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ background: palette.background, boxShadow: palette.boxShadow }}
    >
      <Icon className={iconSize} style={{ color: palette.iconColor }} strokeWidth={2.4} />
    </div>
  );
};

// HeroStrip - clean white page header with a thin bottom border (no gradient banner / glow).
export const HeroStrip = ({
  icon,
  title,
  subtitle,
  eyebrow,
  accent = 'purple',
  actions,
  children,
  glow = true,
  className = '',
  onBack,
}) => (
  <div
    className={`relative bg-white border-b border-line ${className}`}
  >
    <div className="relative px-6 py-5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-4 min-w-0 flex-1">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            title="Back"
            aria-label="Back"
            className="flex-shrink-0 -ml-1 p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        {icon && <GlowIconTile icon={icon} accent={accent} />}
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-1">
              {eyebrow}
            </p>
          )}
          {title && (
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 truncate">{title}</h1>
          )}
          {subtitle && <p className="text-sm text-slate-500 mt-0.5 truncate">{subtitle}</p>}
          {children}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  </div>
);

// StepRail - horizontal step indicator. Done/current = brand green, future = light gray.
export const StepRail = ({ steps, current, label }) => (
  <div className="flex items-center gap-3">
    {steps.map((s, i) => {
      const idx = i + 1;
      const isDone = idx < current;
      const isCurrent = idx === current;
      return (
        <React.Fragment key={s}>
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-all"
            style={
              isDone
                ? { background: '#48b94c', color: '#ffffff' }
                : isCurrent
                ? { background: '#ffffff', color: '#389a3c', border: '2px solid #48b94c' }
                : { background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0' }
            }
          >
            {isDone ? <Check className="w-3.5 h-3.5" /> : idx}
          </div>
          {idx < steps.length && (
            <div
              className="flex-1 h-px"
              style={{
                background: isDone ? '#48b94c' : '#e2e8f0',
                minWidth: '24px',
              }}
            />
          )}
        </React.Fragment>
      );
    })}
    {label && (
      <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-400 ml-2">
        {label}
      </span>
    )}
  </div>
);

// TerminalCard - code block. Intentionally dark (reads as a terminal even on a
// light page, like commercial consoles), but de-neoned.
export const TerminalCard = ({ filename = 'shell', code, copyable = true, children }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code || '').then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div
      className="relative rounded-xl overflow-hidden border border-slate-200"
    >
      <div
        className="flex items-center justify-between px-4 py-2"
        style={{ background: '#0f172a', borderBottom: '1px solid #1e293b' }}
      >
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Terminal className="w-3.5 h-3.5 text-wasabi-400" />
          <span className="font-mono">{filename}</span>
        </div>
        {copyable && code && (
          <button
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md transition-colors"
            style={{
              color: copied ? '#ffffff' : '#6bc46e',
              background: copied ? '#48b94c' : 'transparent',
              border: copied ? 'none' : '1px solid rgba(72, 185, 76, 0.4)',
            }}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        )}
      </div>
      <pre
        className="px-4 py-4 text-xs leading-relaxed overflow-x-auto"
        style={{ background: '#0b1220', color: '#86efac', fontFamily: "'JetBrains Mono', monospace" }}
      >
        {children ?? code}
      </pre>
    </div>
  );
};

// PrimaryCTA - 'green' = solid Wasabi green; 'purple' (secondary chrome) = white + slate border.
export const PrimaryCTA = React.forwardRef(
  (
    {
      variant = 'green',
      icon: Icon,
      iconPosition = 'right',
      children,
      loading = false,
      disabled = false,
      className = '',
      style = {},
      ...rest
    },
    ref,
  ) => {
    const palette = {
      green: { background: '#48b94c', color: '#ffffff', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)' },
      purple: { background: '#ffffff', color: '#334155', boxShadow: 'inset 0 0 0 1px #cbd5e1' },
    }[variant];

    const inactive = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={inactive}
        className={`group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-bold tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
        style={
          inactive
            ? { background: '#e2e8f0', color: '#94a3b8', ...style }
            : { ...palette, ...style }
        }
        {...rest}
      >
        {loading ? (
          <Loader className="w-4 h-4 animate-spin" />
        ) : (
          Icon && iconPosition === 'left' && <Icon className="w-4 h-4" />
        )}
        <span>{children}</span>
        {!loading && Icon && iconPosition === 'right' && (
          <Icon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        )}
      </button>
    );
  },
);
PrimaryCTA.displayName = 'PrimaryCTA';

// EmptyState - hero empty state, light theme.
export const EmptyState = ({
  icon,
  title,
  description,
  actionLabel,
  actionIcon,
  onAction,
  accent = 'green',
  pulse = true,
  className = '',
}) => (
  <div className={`flex flex-col items-center justify-center text-center py-16 px-6 ${className}`}>
    <div className="relative mb-6">
      <GlowIconTile icon={icon} accent={accent} size="lg" />
      {pulse && (
        <div
          className="absolute -inset-1 rounded-xl animate-ping pointer-events-none"
          style={{ background: 'rgba(72, 185, 76, 0.18)', animationDuration: '2.6s' }}
        />
      )}
    </div>
    <h3 className="text-xl font-bold text-slate-900 tracking-tight">{title}</h3>
    {description && (
      <p className="text-sm text-slate-500 mt-2 max-w-md leading-relaxed">{description}</p>
    )}
    {actionLabel && onAction && (
      <PrimaryCTA
        onClick={onAction}
        icon={actionIcon || ChevronRight}
        variant={accent === 'green' ? 'green' : 'purple'}
        className="mt-6"
      >
        {actionLabel}
      </PrimaryCTA>
    )}
  </div>
);

export const SHINOBI_GRADIENTS = { PURPLE_GRADIENT, GREEN_GRADIENT, GREEN_GRADIENT_SOFT };

// ChipInput - multi-value text entry rendering each value as a colored pill.
const CHIP_VARIANTS = {
  cyan: {
    chip: 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100',
    dot: 'bg-cyan-500',
    ring: 'focus-within:border-cyan-400 focus-within:ring-cyan-400/20',
  },
  purple: {
    chip: 'bg-wasabi-50 text-wasabi-700 border-wasabi-200 hover:bg-wasabi-100',
    dot: 'bg-wasabi-500',
    ring: 'focus-within:border-wasabi-400 focus-within:ring-wasabi-400/20',
  },
  pink: {
    chip: 'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100',
    dot: 'bg-pink-500',
    ring: 'focus-within:border-pink-400 focus-within:ring-pink-400/20',
  },
  green: {
    chip: 'bg-wasabi-50 text-wasabi-700 border-wasabi-200 hover:bg-wasabi-100',
    dot: 'bg-wasabi-500',
    ring: 'focus-within:border-wasabi-400 focus-within:ring-wasabi-400/20',
  },
  amber: {
    chip: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
    dot: 'bg-amber-500',
    ring: 'focus-within:border-amber-400 focus-within:ring-amber-400/20',
  },
};

export const ChipInput = ({
  values = [],
  onChange,
  variant = 'cyan',
  placeholder = 'Type and press Enter',
  disabled = false,
  validate,
  emptyHint,
}) => {
  const palette = CHIP_VARIANTS[variant] || CHIP_VARIANTS.cyan;
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const commitTokens = (raw) => {
    if (disabled) return;
    const tokens = String(raw)
      .split(/[\s,;\n]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (!tokens.length) return;
    const next = [...values];
    for (const t of tokens) {
      if (!next.includes(t)) next.push(t);
    }
    onChange(next);
    setDraft('');
  };

  const removeChip = (idx) => {
    if (disabled) return;
    const next = values.filter((_, i) => i !== idx);
    onChange(next);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      if (draft.trim()) {
        e.preventDefault();
        commitTokens(draft);
      }
    } else if (e.key === 'Backspace' && !draft && values.length > 0) {
      e.preventDefault();
      removeChip(values.length - 1);
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (text && /[\s,;\n]/.test(text)) {
      e.preventDefault();
      commitTokens(draft + text);
    }
  };

  const onBlur = () => {
    if (draft.trim()) commitTokens(draft);
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={`min-h-[3rem] rounded-lg border border-slate-300 bg-white px-2 py-1.5 flex flex-wrap items-center gap-1.5 transition focus-within:ring-2 ${palette.ring} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-text'}`}
    >
      {values.length === 0 && !draft && emptyHint && (
        <span className="text-xs text-slate-400 italic px-1">{emptyHint}</span>
      )}
      {values.map((v, i) => {
        const valid = validate ? validate(v) : true;
        return (
          <span
            key={`${v}-${i}`}
            className={`group inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition ${valid ? palette.chip : 'bg-red-50 text-red-700 border-red-200'}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${valid ? palette.dot : 'bg-red-400'}`} />
            <span className="break-all">{v}</span>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeChip(i); }}
                className="opacity-60 hover:opacity-100 transition"
                aria-label={`Remove ${v}`}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        );
      })}
      <input
        ref={inputRef}
        type="text"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={onBlur}
        placeholder={values.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[8rem] bg-transparent outline-none text-slate-800 text-sm py-0.5 placeholder:text-slate-400"
      />
    </div>
  );
};
